const NODE_WIDTH = 288;
const COLUMN_GAP = 160;
const ROW_GAP = 56;
const PADDING = 64;
const MIN_ZOOM = 0.1;
const MAX_ZOOM = 1.5;
const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

export function layoutFlowGraph(nodes, connections) {
    if (!nodes.length) return [];

    const outgoing = new Map(nodes.map((node) => [node.id, []]));
    const incoming = new Map(nodes.map((node) => [node.id, []]));
    [...connections].sort((a, b) => (a.number ?? 0) - (b.number ?? 0)).forEach(({ source, target }) => {
        if (outgoing.has(source) && incoming.has(target) && source !== target) {
            if (!outgoing.get(source).includes(target)) outgoing.get(source).push(target);
            if (!incoming.get(target).includes(source)) incoming.get(target).push(source);
        }
    });
    const remaining = new Set(nodes.map((node) => node.id));
    const ordered = [...nodes].sort((a, b) => Number(b.start) - Number(a.start));
    const components = [];

    ordered.forEach((first) => {
        if (!remaining.has(first.id)) return;
        const ids = new Set([first.id]);
        const queue = [first.id];
        remaining.delete(first.id);
        for (let index = 0; index < queue.length; index++) {
            [...outgoing.get(queue[index]), ...incoming.get(queue[index])].forEach((id) => {
                if (remaining.delete(id)) { ids.add(id); queue.push(id); }
            });
        }
        const members = ordered.filter((node) => ids.has(node.id));
        const byId = new Map(members.map((node) => [node.id, node]));
        const roots = [...members].sort((a, b) => {
            const priority = (node) => node.start ? 0 : !incoming.get(node.id).length ? 1 : node.entry ? 2 : 3;
            return priority(a) - priority(b);
        });
        const state = new Map();
        const forward = new Map(members.map((node) => [node.id, []]));
        const traversal = [];
        roots.forEach((root) => {
            if (state.has(root.id)) return;
            const stack = [{ id: root.id, index: 0 }];
            state.set(root.id, 'visiting');
            traversal.push(root.id);
            while (stack.length) {
                const current = stack.at(-1);
                const targets = outgoing.get(current.id);
                if (current.index === targets.length) {
                    state.set(current.id, 'visited'); stack.pop(); continue;
                }
                const target = targets[current.index++];
                if (byId.get(target).start || state.get(target) === 'visiting') continue;
                forward.get(current.id).push(target);
                if (!state.has(target)) {
                    state.set(target, 'visiting');
                    traversal.push(target);
                    stack.push({ id: target, index: 0 });
                }
            }
        });
        const degrees = new Map(members.map((node) => [node.id, 0]));
        forward.forEach((targets) => targets.forEach((id) => degrees.set(id, degrees.get(id) + 1)));
        const levels = new Map(members.map((node) => [node.id, 0]));
        const ready = traversal.filter((id) => degrees.get(id) === 0);
        for (let index = 0; index < ready.length; index++) {
            forward.get(ready[index]).forEach((id) => {
                levels.set(id, Math.max(levels.get(id), levels.get(ready[index]) + 1));
                degrees.set(id, degrees.get(id) - 1);
                if (!degrees.get(id)) ready.push(id);
            });
        }
        const columns = Array.from({ length: Math.max(...levels.values()) + 1 }, () => []);
        traversal.forEach((id) => columns[levels.get(id)].push(byId.get(id)));
        const heights = columns.map((column) => column.reduce((height, node) => height + node.height, 0) + Math.max(0, column.length - 1) * ROW_GAP);
        components.push({ columns, heights, height: Math.max(...heights) });
    });

    const maximumLevel = Math.max(...components.map(({ columns }) => columns.length - 1));
    let top = PADDING;
    const result = [];
    components.forEach(({ columns, heights, height }) => {
        columns.forEach((column, level) => {
            let y = top + (height - heights[level]) / 2;
            column.forEach((node) => {
                result.push({ ...node, x: PADDING + (maximumLevel - level) * (NODE_WIDTH + COLUMN_GAP), y });
                y += node.height + ROW_GAP;
            });
        });
        top += height + 120;
    });
    return result;
}

const ROUTE_CLEARANCE = 22;
const PORT_LEAD = 28;
const distance = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
const nodeRectangle = (node, padding) => ({ left: node.x - padding, right: node.x + NODE_WIDTH + padding, top: node.y - padding, bottom: node.y + node.height + padding });
const segmentClear = (a, b, rectangles) => !rectangles.some((rect) => a.y === b.y
    ? a.y > rect.top && a.y < rect.bottom && Math.max(a.x, b.x) > rect.left && Math.min(a.x, b.x) < rect.right
    : a.x > rect.left && a.x < rect.right && Math.max(a.y, b.y) > rect.top && Math.min(a.y, b.y) < rect.bottom);

export function measureFlowPort(port, node) {
    let x = port.offsetWidth / 2;
    let y = port.offsetHeight / 2;
    for (let element = port; element && element !== node; element = element.offsetParent) {
        x += element.offsetLeft;
        y += element.offsetTop;
    }
    return { x, y };
}

const connectionPorts = (source, target, offset, offsetX = 0) => ({
    start: { x: source.x + offsetX, y: source.y + offset },
    end: { x: target.x + (target.inputX ?? NODE_WIDTH), y: target.y + (target.inputY ?? 48) },
});

function createRoutePlanner(nodes, endpoints) {
    const rectangles = nodes.map((node) => nodeRectangle(node, ROUTE_CLEARANCE));
    const xs = [...new Set([...rectangles.flatMap((rect) => [rect.left, rect.right]), ...endpoints.map((point) => point.x)])].sort((a, b) => a - b);
    const ys = [...new Set([...rectangles.flatMap((rect) => [rect.top, rect.bottom]), ...endpoints.map((point) => point.y)])].sort((a, b) => a - b);
    const width = xs.length;
    const points = ys.flatMap((y) => xs.map((x) => ({ x, y })));
    const neighbors = points.map(() => []);
    const used = new Map();
    const segmentKey = (a, b) => Math.min(a, b) + ':' + Math.max(a, b);
    points.forEach((point, index) => {
        for (const next of [index % width < width - 1 ? index + 1 : -1, index + width < points.length ? index + width : -1]) {
            if (next < 0 || !segmentClear(point, points[next], rectangles)) continue;
            const horizontal = next === index + 1;
            neighbors[index].push({ index: next, heading: horizontal ? 1 : 3 });
            neighbors[next].push({ index, heading: horizontal ? 0 : 2 });
        }
    });

    return (start, end) => {
        const first = ys.indexOf(start.y) * width + xs.indexOf(start.x);
        const last = ys.indexOf(end.y) * width + xs.indexOf(end.x);
        const costs = new Float64Array(points.length * 4).fill(Infinity);
        const previous = new Int32Array(points.length * 4).fill(-1);
        const heap = [];
        const push = (item) => {
            heap.push(item);
            let index = heap.length - 1;
            while (index > 0) {
                const parent = (index - 1) >> 1;
                if (heap[parent].priority <= item.priority) break;
                heap[index] = heap[parent]; index = parent;
            }
            heap[index] = item;
        };
        const pop = () => {
            const first = heap[0];
            const item = heap.pop();
            if (heap.length) {
                let index = 0;
                while (index * 2 + 1 < heap.length) {
                    let child = index * 2 + 1;
                    if (child + 1 < heap.length && heap[child + 1].priority < heap[child].priority) child++;
                    if (item.priority <= heap[child].priority) break;
                    heap[index] = heap[child]; index = child;
                }
                heap[index] = item;
            }
            return first;
        };
        costs[first * 4] = 0;
        push({ state: first * 4, cost: 0, priority: distance(start, end) });
        while (heap.length) {
            const current = pop();
            if (current.cost !== costs[current.state]) continue;
            const index = Math.floor(current.state / 4);
            const heading = current.state % 4;
            if (index === last && heading !== 1) {
                const path = [];
                for (let state = current.state; state >= 0; state = previous[state]) path.unshift(Math.floor(state / 4));
                path.slice(1).forEach((next, step) => {
                    const key = segmentKey(path[step], next);
                    used.set(key, (used.get(key) ?? 0) + 1);
                });
                return path.map((step) => points[step]);
            }
            neighbors[index].forEach((next) => {
                if ((heading ^ 1) === next.heading) return;
                const length = distance(points[index], points[next.index]);
                const cost = current.cost + length + (heading !== next.heading ? 20 : 0) + (used.get(segmentKey(index, next.index)) ?? 0) * Math.min(length, 48) * 0.4;
                const state = next.index * 4 + next.heading;
                if (cost >= costs[state]) return;
                costs[state] = cost; previous[state] = current.state;
                push({ state, cost, priority: cost + distance(points[next.index], end) });
            });
        }
        return null;
    };
}

function roundedRoute(points) {
    const simplified = [];
    points.forEach((point) => {
        if (simplified.length && !distance(simplified.at(-1), point)) return;
        while (simplified.length > 1) {
            const a = simplified.at(-2);
            const b = simplified.at(-1);
            if (!((a.x === b.x && b.x === point.x) || (a.y === b.y && b.y === point.y))) break;
            simplified.pop();
        }
        simplified.push(point);
    });
    let path = 'M ' + simplified[0].x + ' ' + simplified[0].y;
    simplified.slice(1, -1).forEach((corner, index) => {
        const before = simplified[index];
        const after = simplified[index + 2];
        const radius = Math.min(16, distance(before, corner) / 2, distance(corner, after) / 2);
        const enter = { x: corner.x + Math.sign(before.x - corner.x) * radius, y: corner.y + Math.sign(before.y - corner.y) * radius };
        const leave = { x: corner.x + Math.sign(after.x - corner.x) * radius, y: corner.y + Math.sign(after.y - corner.y) * radius };
        path += ' L ' + enter.x + ' ' + enter.y + ' Q ' + corner.x + ' ' + corner.y + ' ' + leave.x + ' ' + leave.y;
    });
    path += ' L ' + simplified.at(-1).x + ' ' + simplified.at(-1).y;
    const segments = simplified.slice(1).map((point, index) => ({ a: simplified[index], b: point, length: distance(simplified[index], point) }));
    const label = segments.reduce((best, segment) => segment.length * (segment.a.y === segment.b.y ? 1.2 : 1) > best.length * (best.a.y === best.b.y ? 1.2 : 1) ? segment : best);
    return { path, points: simplified, labelX: (label.a.x + label.b.x) / 2, labelY: (label.a.y + label.b.y) / 2 };
}

export function connectionGeometry(source, target, offset = 52, nodes = [source, target], planner = null, offsetX = 0) {
    const { start, end } = connectionPorts(source, target, offset, offsetX);
    const gap = start.x - end.x;
    const bend = clamp(gap / 2, 28, 100);
    const otherCards = nodes.filter((node) => node !== source && node !== target).map((node) => nodeRectangle(node, 14));
    const clearCurve = gap >= PORT_LEAD * 2 && Array.from({ length: 31 }, (_, index) => {
        const t = (index + 1) / 32;
        const u = 1 - t;
        const x = u ** 3 * start.x + 3 * u ** 2 * t * (start.x - bend) + 3 * u * t ** 2 * (end.x + bend) + t ** 3 * end.x;
        const y = u ** 3 * start.y + 3 * u ** 2 * t * start.y + 3 * u * t ** 2 * end.y + t ** 3 * end.y;
        return !otherCards.some((rect) => x > rect.left && x < rect.right && y > rect.top && y < rect.bottom);
    }).every(Boolean);
    let geometry;
    if (clearCurve) {
        geometry = { path: 'M ' + start.x + ' ' + start.y + ' C ' + (start.x - bend) + ' ' + start.y + ', ' + (end.x + bend) + ' ' + end.y + ', ' + end.x + ' ' + end.y,
            points: [start, end], labelX: (start.x + end.x) / 2, labelY: (start.y + end.y) / 2 };
    } else {
        const exit = { x: start.x - PORT_LEAD, y: start.y };
        const entry = { x: end.x + PORT_LEAD, y: end.y };
        const route = (planner ?? createRoutePlanner(nodes, [exit, entry]))(exit, entry);
        const top = Math.min(...nodes.map((node) => node.y)) - ROUTE_CLEARANCE;
        geometry = roundedRoute([start, ...(route ?? [exit, { x: exit.x, y: top }, { x: entry.x, y: top }, entry]), end]);
    }
    return { ...geometry, bounds: {
        left: Math.min(...geometry.points.map((point) => point.x)), right: Math.max(...geometry.points.map((point) => point.x)),
        top: Math.min(...geometry.points.map((point) => point.y)), bottom: Math.max(...geometry.points.map((point) => point.y)),
    } };
}

export function connectionPath(source, target, offset = 52, nodes = [source, target]) {
    return connectionGeometry(source, target, offset, nodes).path;
}

export function botFlowCanvas(options = {}) {
    let resizeObserver;
    let mutationObserver;
    let refreshFrame = 0;
    let cameraFrame = 0;
    let cameraTarget = null;
    let geometryKey = '';
    let geometries = new Map();
    let drag = null;
    let viewportSize = '';
    let suppressClick = false;
    let clickTimer;
    let destroyed = false;
    let saveTimer;
    let layoutStatusTimer;
    let savedPositions = new Map();
    const storageKey = options.layoutKey ? 'roobito:flow-layout:v1:' + options.layoutKey : null;
    const pointers = new Map();
    const observedNodes = new Set();

    return {
        nodes: [], connections: [], ready: false, selected: '', activeEdge: '', scale: 1,
        panX: 0, panY: 0, worldWidth: 1000, worldHeight: 800,
        expanded: false, moving: false, nodesMovable: false, panEnabled: false, announcement: '',
        readOnly: Boolean(options.readOnly), manualLayout: false, layoutStatus: '',
        previewFlowId: '', previewSteps: [], followPreview: true, nodeMenu: null, nodeMenuOpen: false,

        init() {
            this.loadLayout();
            this.$watch('nodesMovable', (enabled) => {
                this.cancelPointers();
                this.announcement = enabled ? 'جابجایی بخش‌ها فعال شد.' : 'جای بخش‌ها قفل شد.';
            });
            this.$watch('panEnabled', (enabled) => {
                this.cancelPointers();
                this.announcement = enabled ? 'حرکت روی نقشه فعال شد.' : 'حرکت روی نقشه غیرفعال شد؛ پیمایش صفحه فعال است.';
            });
            this.$watch('followPreview', (enabled) => { if (enabled) this.followCurrentStep(); });
            this.$nextTick(() => {
                if (destroyed) return;
                this.refresh(true);
                this.tracePreview({ botId: options.botId, flowId: options.currentFlowId, steps: options.initialTrace ?? [] });
                resizeObserver = new ResizeObserver(() => this.scheduleRefresh());
                resizeObserver.observe(this.$refs.viewport);
                this.$refs.nodes.querySelectorAll('[data-flow-node]').forEach((node) => resizeObserver.observe(node));
                mutationObserver = new MutationObserver(() => this.scheduleRefresh());
                mutationObserver.observe(this.$refs.nodes, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-flow-target', 'data-flow-port', 'data-flow-conditional', 'data-flow-start', 'data-flow-entry', 'data-flow-label'] });
            });
        },
        destroy() {
            if (saveTimer || (drag?.type === 'node' && drag.moved)) this.saveLayout();
            destroyed = true;
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            cancelAnimationFrame(refreshFrame);
            this.stopCamera();
            clearTimeout(clickTimer);
            clearTimeout(saveTimer);
            clearTimeout(layoutStatusTimer);
            pointers.clear();
            observedNodes.clear();
            geometries.clear();
        },
        stopCamera() { cancelAnimationFrame(cameraFrame); cameraFrame = 0; cameraTarget = null; },
        loadLayout() {
            if (!storageKey) return;
            try {
                const layout = JSON.parse(window.localStorage.getItem(storageKey));
                if (layout?.version !== 1 || !Array.isArray(layout.positions)) return;
                savedPositions = new Map(layout.positions.slice(0, 10000).filter((position) =>
                    typeof position?.id === 'string' && Number.isFinite(position.x) && Number.isFinite(position.y) &&
                    position.x >= PADDING && position.y >= PADDING && position.x <= 1000000 && position.y <= 1000000
                ).map((position) => [position.id, { x: position.x, y: position.y }]));
                this.manualLayout = savedPositions.size > 0;
                if (this.manualLayout) this.layoutStatus = 'چیدمان شخصی';
            } catch { this.layoutStatus = 'ذخیرهٔ چیدمان در این مرورگر در دسترس نیست.'; }
        },
        restorePositions(nodes) {
            if (!this.manualLayout) return nodes;
            const placed = [];
            [...nodes].sort((a, b) => Number(savedPositions.has(b.id)) - Number(savedPositions.has(a.id))).forEach((node) => {
                const position = { ...node, ...(savedPositions.get(node.id) ?? {}) };
                let collision;
                while ((collision = placed.find((other) => Math.abs(other.x - position.x) < NODE_WIDTH + 24 &&
                    position.y < other.y + other.height + 24 && position.y + position.height + 24 > other.y))) {
                    position.y = collision.y + collision.height + ROW_GAP;
                }
                placed.push(position);
            });
            const positions = new Map(placed.map((node) => [node.id, node]));
            return nodes.map((node) => positions.get(node.id));
        },
        saveLayout() {
            clearTimeout(saveTimer); saveTimer = null;
            if (this.readOnly || !storageKey || destroyed) return;
            clearTimeout(layoutStatusTimer);
            savedPositions = new Map(this.nodes.map(({ id, x, y }) => [id, { x, y }]));
            this.manualLayout = true;
            try {
                window.localStorage.setItem(storageKey, JSON.stringify({ version: 1, positions: this.nodes.map(({ id, x, y }) => ({ id, x, y })) }));
                this.layoutStatus = 'چیدمان ذخیره شد';
                layoutStatusTimer = setTimeout(() => {
                    if (!destroyed && this.layoutStatus === 'چیدمان ذخیره شد') this.layoutStatus = '';
                    layoutStatusTimer = null;
                }, 3000);
            } catch { this.layoutStatus = 'ذخیرهٔ چیدمان در این مرورگر در دسترس نیست.'; }
        },
        queueLayoutSave() {
            if (this.readOnly || !storageKey) return;
            clearTimeout(saveTimer);
            saveTimer = setTimeout(() => this.saveLayout(), 250);
        },
        tracePreview(detail) {
            if (String(detail?.botId) !== String(options.botId)) return;
            const steps = Array.isArray(detail.steps) ? detail.steps.slice(-100) : [];
            this.previewSteps = steps.filter((step) => step && Number.isInteger(step.flowId) && (step.edgeId === null || typeof step.edgeId === 'string'));
            this.previewFlowId = detail.flowId == null ? '' : String(detail.flowId);
            const node = this.nodes.find((node) => node.id === this.previewFlowId);
            if (this.previewSteps.length) this.announcement = node ? 'پیش‌نمایش: ' + node.label : 'این مرحله در فهرست‌های گروهی قرار دارد.';
            this.followCurrentStep();
        },
        clearPreview(detail) {
            if (String(detail?.botId) !== String(options.botId)) return;
            this.previewSteps = []; this.previewFlowId = '';
        },
        followCurrentStep() {
            if (destroyed || !this.ready || !this.readOnly || !this.followPreview || drag || this.$refs.viewport.clientWidth === 0) return;
            if (this.nodes.some((node) => node.id === this.previewFlowId)) this.focusNode(this.previewFlowId);
        },
        get previewLabel() { return this.nodes.find((node) => node.id === this.previewFlowId)?.label ?? (this.previewFlowId ? 'فهرست گروهی' : 'نقشهٔ گفتگو'); },
        get visitedNodes() { return new Set(this.previewSteps.map((step) => String(step.flowId))); },
        scheduleRefresh() {
            if (destroyed) return;
            cancelAnimationFrame(refreshFrame);
            refreshFrame = requestAnimationFrame(() => this.refresh());
        },
        refresh(initial = false) {
            if (destroyed) return;
            const previous = new Map(this.nodes.map((node) => [node.id, node]));
            const elements = [...this.$refs.nodes.querySelectorAll('[data-flow-node]')];
            observedNodes.forEach((element) => {
                if (!elements.includes(element)) { resizeObserver?.unobserve(element); observedNodes.delete(element); }
            });
            const nodes = elements.map((element) => {
                const inputPort = element.querySelector('[data-flow-input]');
                const input = inputPort ? measureFlowPort(inputPort, element) : { x: NODE_WIDTH, y: 48 };
                return {
                    id: element.dataset.flowNode, label: element.dataset.flowLabel,
                    start: element.dataset.flowStart === 'true', entry: element.dataset.flowEntry === 'true',
                    height: element.offsetHeight, inputX: input.x, inputY: input.y,
                };
            });
            const ids = new Set(nodes.map((node) => node.id));
            const connections = [];
            elements.forEach((element) => {
                element.querySelectorAll('[data-flow-target]').forEach((port, index) => {
                    if (!ids.has(port.dataset.flowTarget)) return;
                    const outputPort = port.querySelector('[data-flow-output]');
                    if (!outputPort) return;
                    const output = measureFlowPort(outputPort, element);
                    connections.push({
                        id: port.dataset.flowPort, source: element.dataset.flowNode, target: port.dataset.flowTarget,
                        offset: output.y, offsetX: output.x, number: index + 1, label: port.dataset.flowLabel, conditional: port.dataset.flowConditional === 'true',
                    });
                });
                resizeObserver?.observe(element);
                observedNodes.add(element);
            });
            const structureChanged = nodes.length !== previous.size || nodes.some((node) => !previous.has(node.id));
            const heightChanged = nodes.some((node) => previous.has(node.id) && previous.get(node.id).height !== node.height);
            const routeOrder = (edges) => edges.map((edge) => [edge.source, edge.target, edge.number].join(':')).join('|');
            const routesChanged = routeOrder(connections) !== routeOrder(this.connections);
            if (structureChanged) this.cancelPointers();
            this.connections = connections;
            if (!connections.some((edge) => edge.id === this.activeEdge)) this.activeEdge = '';
            this.nodes = initial || structureChanged || ((heightChanged || routesChanged) && !this.nodesMovable)
                ? this.restorePositions(layoutFlowGraph(nodes, connections))
                : nodes.map((node) => ({ ...node, x: previous.get(node.id).x, y: previous.get(node.id).y }));
            this.updateBounds();
            this.ready = true;
            const size = this.$refs.viewport.clientWidth + ':' + this.$refs.viewport.clientHeight;
            const resized = viewportSize && viewportSize !== size;
            viewportSize = size;
            if (initial) this.$nextTick(() => {
                if (destroyed) return;
                if (this.$refs.viewport.clientWidth >= 640) this.fit(false);
                else this.focusStart(false);
                this.followCurrentStep();
            });
            else if (structureChanged) {
                const added = nodes.find((node) => !previous.has(node.id));
                if (added) this.$nextTick(() => { if (!destroyed) this.focusNode(added.id); });
                else if (!ids.has(this.selected)) { this.selected = ''; this.fit(false); }
            } else if (resized && !drag) {
                this.stopCamera();
                this.boundPan();
                this.followCurrentStep();
            }
        },
        get graphBounds() {
            if (!this.nodes.length) return { left: 0, top: 0, right: 800, bottom: 600 };
            return {
                left: Math.min(...this.nodes.map((node) => node.x)),
                top: Math.min(...this.nodes.map((node) => node.y)),
                right: Math.max(...this.nodes.map((node) => node.x + NODE_WIDTH)),
                bottom: Math.max(...this.nodes.map((node) => node.y + node.height)),
            };
        },
        edgeMarkup(arrowId) {
            const label = (value) => String(value).replace(/[<>&"]/g, '');
            return this.edges.map((edge) => {
                const stroke = edge.highlighted ? 'stroke-emerald-600 dark:stroke-emerald-400' : 'stroke-zinc-400 dark:stroke-zinc-500';
                const labelStroke = edge.highlighted ? 'stroke-emerald-600 dark:stroke-emerald-400' : 'stroke-zinc-300 dark:stroke-zinc-600';
                return '<g opacity="' + (edge.muted ? 0.25 : 1) + '">'
                    + '<path d="' + edge.path + '" class="' + stroke + '" stroke-width="' + (edge.traced ? 2.5 : 1.5) + '" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"'
                    + (edge.conditional ? ' stroke-dasharray="5 4"' : '') + ' marker-end="url(#' + arrowId + ')" />'
                    + '<g transform="translate(' + edge.labelX + ',' + edge.labelY + ')">'
                    + '<rect x="-11" y="-10" width="22" height="20" rx="6" class="fill-white dark:fill-zinc-900 ' + labelStroke + '" />'
                    + '<text text-anchor="middle" dominant-baseline="central" font-size="11" class="fill-zinc-700 dark:fill-zinc-200">' + label(edge.number) + '</text>'
                    + '</g></g>';
            }).join('');
        },
        get edges() {
            const nodes = new Map(this.nodes.map((node) => [node.id, node]));
            const connections = this.connections.filter((edge) => nodes.has(edge.source) && nodes.has(edge.target));
            const taken = new Set();
            const returns = new Map();
            this.previewSteps.forEach((step, index) => {
                const source = String(this.previewSteps[index - 1]?.flowId ?? '');
                const target = String(step.flowId);
                if (!step.edgeId || !nodes.has(source) || !nodes.has(target)) return;
                const edge = connections.find((edge) => edge.id === step.edgeId && edge.source === source && edge.target === target);
                if (edge) taken.add(edge.id);
                else if (step.edgeId.startsWith('button-')) {
                    const id = 'preview-return:' + source + ':' + target;
                    returns.set(id, { id, source, target, offset: 80, number: '↩', conditional: false });
                    taken.add(id);
                }
            });
            connections.push(...returns.values());
            const key = this.nodes.map((node) => [node.id, node.x, node.y, node.height, node.inputX, node.inputY].join(':')).join('|') + '/' +
                connections.map((edge) => [edge.id, edge.source, edge.target, edge.offset, edge.offsetX].join(':')).join('|');
            if (key !== geometryKey) {
                const endpoints = connections.flatMap((edge) => {
                    const { start, end } = connectionPorts(nodes.get(edge.source), nodes.get(edge.target), edge.offset, edge.offsetX);
                    return [{ x: start.x - PORT_LEAD, y: start.y }, { x: end.x + PORT_LEAD, y: end.y }];
                });
                let routePlanner;
                const planner = (start, end) => {
                    routePlanner ??= createRoutePlanner(this.nodes, endpoints);
                    return routePlanner(start, end);
                };
                geometries = new Map(connections.map((edge) => [edge.id, connectionGeometry(nodes.get(edge.source), nodes.get(edge.target), edge.offset, this.nodes, planner, edge.offsetX)]));
                geometryKey = key;
            }
            return connections.map((edge) => {
                return {
                    ...edge, ...geometries.get(edge.id),
                    traced: taken.has(edge.id),
                    highlighted: taken.has(edge.id) || (this.activeEdge ? this.activeEdge === edge.id : this.selected === edge.source),
                    muted: this.previewSteps.length ? !taken.has(edge.id) : this.activeEdge && this.activeEdge !== edge.id,
                };
            });
        },
        nodeStyle(id) {
            const node = this.nodes.find((item) => item.id === String(id));
            return node ? 'left:' + node.x + 'px;top:' + node.y + 'px' : 'left:0;top:0';
        },
        get contentBounds() {
            const bounds = { ...this.graphBounds };
            this.edges.forEach(({ bounds: route }) => {
                bounds.left = Math.min(bounds.left, route.left - 12);
                bounds.right = Math.max(bounds.right, route.right + 12);
                bounds.top = Math.min(bounds.top, route.top - 12);
                bounds.bottom = Math.max(bounds.bottom, route.bottom + 12);
            });
            return bounds;
        },
        updateBounds() {
            const bounds = this.contentBounds;
            this.worldWidth = Math.max(800, bounds.right + PADDING);
            this.worldHeight = Math.max(600, bounds.bottom + PADDING);
        },
        setCamera(camera, animate = false) {
            this.stopCamera();
            if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                Object.assign(this, camera);
                return;
            }
            const from = { scale: this.scale, panX: this.panX, panY: this.panY };
            cameraTarget = camera;
            let started;
            const tick = (time) => {
                started ??= time;
                const progress = Math.min(1, (time - started) / 180);
                const eased = 1 - Math.pow(1 - progress, 3);
                Object.keys(from).forEach((key) => { this[key] = from[key] + (camera[key] - from[key]) * eased; });
                if (progress < 1) cameraFrame = requestAnimationFrame(tick);
                else { cameraFrame = 0; cameraTarget = null; }
            };
            cameraFrame = requestAnimationFrame(tick);
        },
        fit(animate = true) {
            if (!this.nodes.length) return;
            const bounds = this.contentBounds;
            const viewport = this.$refs.viewport;
            const scale = clamp(Math.min((viewport.clientWidth - 64) / (bounds.right - bounds.left), (viewport.clientHeight - 64) / (bounds.bottom - bounds.top)), MIN_ZOOM, 1);
            this.setCamera({
                scale,
                panX: (viewport.clientWidth - (bounds.right - bounds.left) * scale) / 2 - bounds.left * scale,
                panY: (viewport.clientHeight - (bounds.bottom - bounds.top) * scale) / 2 - bounds.top * scale,
            }, animate);
        },
        selectNode(id) {
            const node = this.nodes.find((item) => item.id === String(id));
            if (!node) return;
            this.selected = node.id;
            this.announcement = 'بخش ' + node.label + ' انتخاب شد.';
        },
        focusNode(id, animate = true) {
            const node = this.nodes.find((item) => item.id === String(id));
            if (!node) return;
            this.selectNode(id);
            const viewport = this.$refs.viewport;
            const scale = clamp(Math.min(1, (viewport.clientWidth - 48) / NODE_WIDTH,
                this.readOnly ? (viewport.clientHeight - 48) / node.height : 1), MIN_ZOOM, 1);
            this.setCamera({
                scale,
                panX: viewport.clientWidth / 2 - (node.x + NODE_WIDTH / 2) * scale,
                panY: Math.max(24, (viewport.clientHeight - node.height * scale) / 2) - node.y * scale,
            }, animate);
        },
        focusStart(animate = true) { this.focusNode(this.nodes.find((node) => node.start)?.id ?? this.nodes[0]?.id, animate); },
        resetViewportScroll() {
            const viewport = this.$refs.viewport;
            if (viewport.scrollLeft) viewport.scrollLeft = 0;
            if (viewport.scrollTop) viewport.scrollTop = 0;
        },
        focusWithin(event) {
            this.resetViewportScroll();
            if (event.target.closest('[popover]')) return;
            const element = event.target.closest('[data-flow-node]');
            if (!element || drag || !event.target.matches(':focus-visible')) return;
            this.selectNode(element.dataset.flowNode);
            const node = this.nodes.find((item) => item.id === element.dataset.flowNode);
            if (!node) return;
            const viewport = this.$refs.viewport;
            const bounds = viewport.getBoundingClientRect();
            const focused = event.target.getBoundingClientRect();
            const adjust = (low, high, minimum, maximum) => low < minimum ? minimum - low : high > maximum ? maximum - high : 0;
            const x = adjust(focused.left, focused.right, bounds.left + 16, bounds.left + viewport.clientWidth - 16);
            const y = adjust(focused.top, focused.bottom, bounds.top + 16, bounds.top + viewport.clientHeight - 16);
            if (x || y) this.setCamera({ scale: this.scale, panX: this.panX + x, panY: this.panY + y });
        },
        boundPan() {
            const bounds = this.contentBounds;
            const viewport = this.$refs.viewport;
            const constrain = (pan, low, high, size) => {
                const margin = Math.min(96, size / 3);
                return clamp(pan, margin - high * this.scale, size - margin - low * this.scale);
            };
            this.panX = constrain(this.panX, bounds.left - 48, bounds.right + 48, viewport.clientWidth);
            this.panY = constrain(this.panY, bounds.top, this.worldHeight - 64, viewport.clientHeight);
        },
        zoom(amount, clientX = null, clientY = null, animate = false) {
            const base = animate && cameraTarget ? cameraTarget : { scale: this.scale, panX: this.panX, panY: this.panY };
            this.stopCamera();
            const rect = this.$refs.viewport.getBoundingClientRect();
            const x = clientX === null ? rect.width / 2 : clientX - rect.left;
            const y = clientY === null ? rect.height / 2 : clientY - rect.top;
            const scale = clamp(base.scale * amount, MIN_ZOOM, MAX_ZOOM);
            this.setCamera({
                scale, panX: x - (x - base.panX) * scale / base.scale,
                panY: y - (y - base.panY) * scale / base.scale,
            }, animate);
        },
        wheel(event) {
            if (event.target?.closest('[popover]')) return;
            if (drag) { event.preventDefault(); return; }
            const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.$refs.viewport.clientHeight : 1;
            const deltaX = event.deltaX * unit;
            const deltaY = event.deltaY * unit;
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
                this.zoom(Math.exp(-clamp(deltaY, -100, 100) * 0.005), event.clientX, event.clientY);
            } else if (this.panEnabled) {
                event.preventDefault();
                this.stopCamera();
                this.panX -= event.shiftKey ? deltaX || deltaY : deltaX;
                this.panY -= event.shiftKey ? 0 : deltaY;
                this.boundPan();
            } else if (this.expanded) {
                event.preventDefault();
            }
        },
        pointerDown(event) {
            const middleButton = event.button === 1;
            if ((!middleButton && event.button !== 0) || event.target.closest('[popover]')) return;
            const interactive = event.target.closest('button, a, input, select, [role="button"]');
            if (!middleButton && interactive && !interactive.matches('[data-node-drag]')) return;
            const handle = middleButton || !this.nodesMovable || this.readOnly ? null : event.target.closest('[data-node-drag]');
            event.preventDefault();
            if (pointers.size >= 2 || drag?.type === 'node') return;
            this.stopCamera();
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            event.currentTarget.setPointerCapture(event.pointerId);
            if (pointers.size === 2) {
                const points = [...pointers.values()];
                drag = { type: 'pinch', distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y), centerX: (points[0].x + points[1].x) / 2, centerY: (points[0].y + points[1].y) / 2 };
            } else {
                const element = event.target.closest('[data-flow-node]');
                const node = handle ? this.nodes.find((item) => item.id === handle.dataset.nodeDrag) : null;
                if (element) this.selectNode(element.dataset.flowNode);
                drag = { type: node ? 'node' : 'pan', id: node?.id, x: event.clientX, y: event.clientY, originX: node?.x ?? this.panX, originY: node?.y ?? this.panY, moved: false };
            }
            this.moving = true;
        },
        pointerMove(event) {
            if (!pointers.has(event.pointerId) || !drag) return;
            pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (drag.type === 'pan' && event.pointerType === 'touch' && !this.expanded && !this.panEnabled && !this.nodesMovable &&
                Math.abs(event.clientY - drag.y) > Math.abs(event.clientX - drag.x)) return;
            event.preventDefault();
            if (drag.type === 'pinch' && pointers.size === 2) {
                const points = [...pointers.values()];
                const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
                const centerX = (points[0].x + points[1].x) / 2;
                const centerY = (points[0].y + points[1].y) / 2;
                if (drag.distance > 0) this.zoom(distance / drag.distance, centerX, centerY);
                this.panX += centerX - drag.centerX;
                this.panY += centerY - drag.centerY;
                drag = { type: 'pinch', distance, centerX, centerY, moved: true };
            } else if (Math.hypot(event.clientX - drag.x, event.clientY - drag.y) > 4 || drag.moved) {
                drag.moved = true;
                if (drag.type === 'node') {
                    if (!this.nodesMovable) { this.cancelPointers(); return; }
                    const node = this.nodes.find((item) => item.id === drag.id);
                    if (!node) { this.cancelPointers(); return; }
                    node.x = Math.max(PADDING, drag.originX + (event.clientX - drag.x) / this.scale);
                    node.y = Math.max(PADDING, drag.originY + (event.clientY - drag.y) / this.scale);
                    this.updateBounds();
                } else {
                    this.panX = drag.originX + event.clientX - drag.x;
                    this.panY = drag.originY + event.clientY - drag.y;
                    this.boundPan();
                }
            }
        },
        pointerUp(event) {
            if (!pointers.has(event.pointerId)) return;
            if (drag?.type === 'node' && drag.moved) this.saveLayout();
            suppressClick = Boolean(drag?.moved);
            clearTimeout(clickTimer);
            clickTimer = setTimeout(() => { suppressClick = false; }, 0);
            pointers.delete(event.pointerId);
            if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            this.continuePointers();
        },
        continuePointers() {
            if (pointers.size === 1) {
                const point = [...pointers.values()][0];
                drag = { type: 'pan', x: point.x, y: point.y, originX: this.panX, originY: this.panY, moved: true };
            } else { drag = null; this.moving = false; }
        },
        cancelPointers() {
            if (drag?.type === 'node' && drag.moved) this.saveLayout();
            pointers.clear(); drag = null; this.moving = false;
        },
        prepareContextMenu() {
            this.cancelPointers();
            this.stopCamera();
            this.$nextTick(() => {
                if (!destroyed) this.$refs.contextMenu.querySelector('[role="menuitem"]')?.focus();
            });
        },
        keyMoveNode(event) {
            const id = event.target.closest?.('[data-node-key-move]')?.dataset.nodeKeyMove;
            const step = { ArrowLeft: [-20, 0], ArrowRight: [20, 0], ArrowUp: [0, -20], ArrowDown: [0, 20] }[event.key];
            if (!id || !step) return;
            event.preventDefault();
            event.stopPropagation();
            this.moveNode(Number(id), ...step);
        },
        openNodeMenu(event, id) {
            const rect = event.currentTarget.getBoundingClientRect();
            const node = event.currentTarget.closest('[data-flow-node]');
            this.cancelPointers();
            this.nodeMenu = {
                id,
                active: node?.dataset.flowActive === 'true',
                x: Math.min(window.innerWidth - 184, Math.max(8, rect.left)),
                y: Math.min(window.innerHeight - 112, rect.bottom + 4),
            };
            this.nodeMenuOpen = true;
            this.$nextTick(() => this.$refs.nodeMenu?.querySelector('[role="menuitem"]')?.focus());
        },
        openCanvasMenu(event) {
            const target = event.target.closest('button') ?? this.$refs.viewport;
            const rect = target.getBoundingClientRect();
            this.$refs.contextArea.dispatchEvent(new MouseEvent('contextmenu', {
                bubbles: true, cancelable: true,
                clientX: rect.left + 24,
                clientY: target === this.$refs.viewport ? rect.top + 24 : rect.bottom + 4,
            }));
        },
        contextMenuKey(event) {
            if (event.key !== 'ContextMenu' && !(event.shiftKey && event.key === 'F10')) return;
            event.preventDefault();
            this.openCanvasMenu(event);
        },
        pointerLost(event) {
            if (pointers.delete(event.pointerId)) this.continuePointers();
        },
        canvasClick(event) {
            if (suppressClick) { event.preventDefault(); event.stopPropagation(); }
        },
        moveNode(id, x, y) {
            if (!this.nodesMovable || this.readOnly) return;
            const node = this.nodes.find((item) => item.id === String(id));
            if (!node) return;
            this.stopCamera();
            this.selectNode(id);
            node.x = Math.max(PADDING, node.x + x);
            node.y = Math.max(PADDING, node.y + y);
            this.updateBounds();
            this.queueLayoutSave();
        },
        keyPan(event) {
            if (event.target !== this.$refs.viewport) return;
            const moves = { ArrowLeft: [64, 0], ArrowRight: [-64, 0], ArrowUp: [0, 64], ArrowDown: [0, -64] };
            if (moves[event.key]) {
                event.preventDefault(); this.stopCamera();
                this.panX += moves[event.key][0]; this.panY += moves[event.key][1]; this.boundPan();
            } else if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoom(1.2, null, null, true); }
            else if (event.key === '-') { event.preventDefault(); this.zoom(1 / 1.2, null, null, true); }
            else if (event.key === '0') { event.preventDefault(); this.fit(); }
        },
        arrange() {
            this.cancelPointers(); clearTimeout(saveTimer); saveTimer = null;
            clearTimeout(layoutStatusTimer); layoutStatusTimer = null;
            let cleared = true;
            if (!this.readOnly && storageKey) {
                try { window.localStorage.removeItem(storageKey); }
                catch { cleared = false; }
            }
            savedPositions.clear(); this.manualLayout = false; this.layoutStatus = cleared ? 'چیدمان خودکار' : 'چیدمان خودکار؛ پاک کردن ذخیرهٔ مرورگر در دسترس نیست.'; this.nodesMovable = false;
            this.nodes = layoutFlowGraph(this.nodes, this.connections); this.updateBounds(); this.fit();
        },
        toggleExpanded() { this.cancelPointers(); this.expanded = !this.expanded; this.$nextTick(() => { if (!destroyed) this.fit(); }); },
        escapeExpanded(event) {
            if (!this.expanded || event.defaultPrevented || event.target.closest('dialog, [popover]')) return;
            event.preventDefault();
            this.toggleExpanded();
        },
    };
}
